/* eslint-disable @typescript-eslint/naming-convention */
import axios, { CancelTokenSource } from 'axios';
import { useRef, useState } from 'react';

import Config from '@config/config';
import { generateNewCancelTokenSource } from '@services/axios/axiosUtils';

export default function useKonnect() {
    const [paymentState, setPaymentState]     = useState<KonnectInitPaymentResponse | null>(null);
    const [paymentDetails, setPaymentDetails] = useState<KonnectPaymentDetailsResponse | null>(null);
    const [isLoading, setIsLoading]           = useState(false);
    const [hasError, setHasError]             = useState(false);

    const tokenSource                     = useRef<CancelTokenSource>(null);

    const initPayment = async (options: Omit<KonnectInitPaymentBody, 'receiverWalletId' | 'acceptedPaymentMethods' | 'token'>) => {
        setIsLoading(true);

        if (tokenSource.current) {
            tokenSource.current.cancel();
        }

        tokenSource.current = generateNewCancelTokenSource();

        try {
            const response = await axios.post<KonnectInitPaymentResponse>(
                `${Config.api.konnectBaseUrl}/api/v2/payments/init-payment`,
                {
                    ...options,
                    receiverWalletId: Config.api.konnectWalletId,
                    acceptedPaymentMethods: [
                        'bank_card',
                        'e-DINAR',
                    ],
                    token: 'USD',
                } as KonnectInitPaymentBody,
                {
                    headers: {
                        'x-api-key': Config.api.konnectKey
                    },
                    cancelToken: tokenSource.current.token
                }
            );

            if (response.status === 200) {
                setPaymentState(response.data);
                setHasError(false);

                return response.data;
            }

            throw new Error('Failed to initialize payment!');
        } catch (e) {
            console.error(e);
            setHasError(true);
            setIsLoading(false);

            return null;
        }
    };

    const getPaymentDetails = async (paymentId: string) => {
        setIsLoading(true);

        if (tokenSource.current) {
            tokenSource.current.cancel();
        }

        tokenSource.current = generateNewCancelTokenSource();

        try {
            const response = await axios.get<KonnectPaymentDetailsResponse>(
                `${Config.api.konnectBaseUrl}/api/v2/payments/${paymentId}`,
                {
                    headers: {
                        'x-api-key': Config.api.konnectKey
                    },
                    cancelToken: tokenSource.current.token
                }
            );

            if (response.status === 200) {
                setHasError(false);
                setPaymentDetails(response.data);

                return response.data;
            }

            throw new Error('Failed to get payment details!');
        } catch (e) {
            console.error(e);
            setHasError(true);

            return null;
        }
        finally {
            setIsLoading(false);
        }
    };

    return {
        isLoading,
        hasError,
        setIsLoading,
        initPayment,
        paymentState,
        getPaymentDetails,
        paymentDetails,
    };
}

type PaymentMethod =
'bank_card' |
'e-DINAR' |
'wallet' |
'cash' |
'nfc' |
'izi' |
'pluxee';
type PaymentCurrency = 'TND' | 'EUR' | 'USD';
type PaymentStatus = 'completed' | 'pending';

interface KonnectPaymentDetailsResponse {
    payment: {
        id: string;
        status: PaymentStatus;
        amountDue: string;
        reachedAmount: number;
        amount: number;
        token: PaymentCurrency;
        convertedAmount: number;
        exchangeRate: number;
        expirationDate: string;
        shortId: string;
        link: string;
        webhook: string;
        successUrl: string;
        failUrl: string;
        orderId: string;
        type: string;
        details: string;
        acceptedPaymentMethods: PaymentMethod[];
        receiverWallet: {
            // Wallet details here (truncated for brevity)
        };
        transactions: {
            type: string;
            method: PaymentMethod;
            status: PaymentStatus;
            token: PaymentCurrency;
            amount: number;
            ext_payment_ref: string;
            from: string;
            id: string;
        }[];
    };
}

interface KonnectInitPaymentResponse {
    payUrl: string;
    paymentRef: string;
}

interface KonnectInitPaymentBody {
    receiverWalletId: string;
    amount: number;
    email: string;
    token?: PaymentCurrency;
    type?:  string;
    description?: string;
    acceptedPaymentMethods?: PaymentMethod[];
    lifespan?: number;
    checkoutForm?: boolean;
    addPaymentFeesToAmount?: boolean;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    orderId?: string;
    webhook?: string;
    theme?: 'dark';
}
